import { useEffect, useState } from "react";
import { Avatar, Badge, Box, Button, Center, Flex, Loader, Skeleton, Text, Title, Tooltip } from "@mantine/core";
import { IconClock, IconLogout, IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc, { removeToken } from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { formatINR } from "@/shared/utils/format.utils";
import LogoutModal from "./LogoutModal";

type AdminProfile = {
  id: string;
  username: string;
};

type PortfolioStats = {
  totalInvested: number;
  currentValue: number;
  xirr: number | null;
};

export default function ProfilePage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [logoutOpened, setLogoutOpened] = useState(false);
  const [statsLoadedAt, setStatsLoadedAt] = useState<Date | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.getCurrentAdmin.query();
        setProfile({ id: res.data.id, username: res.data.username });
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await trpc.admin.getPortfolioStats.query();
        setStats(res.data);
        setStatsLoadedAt(new Date());
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleLogout = () => {
    removeToken();
    navigate("/login", { replace: true });
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  const initial = profile?.username?.charAt(0).toUpperCase() ?? "A";

  const xirrValue = stats?.xirr ?? null;
  const isPositiveXirr = xirrValue !== null && xirrValue >= 0;
  const absoluteReturn = stats != null ? stats.currentValue - stats.totalInvested : null;
  const absoluteReturnPct =
    stats != null && stats.totalInvested > 0
      ? ((stats.currentValue - stats.totalInvested) / stats.totalInvested) * 100
      : null;

  return (
    <Flex direction="column" w="100%" p="xl">
      {/* ── Header ── */}
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={2} fw={700}>
          Profile
        </Title>
        <Button color="primary" leftSection={<IconLogout size={16} />} onClick={() => setLogoutOpened(true)}>
          Log Out
        </Button>
      </Flex>

      {/* ── Admin info ── */}
      <Flex align="center" gap="md" mb="xl">
        <Avatar size={48} radius="xl" color="#1F68A8" variant="filled">
          {initial}
        </Avatar>
        <Text fw={700} fz="lg">
          {profile?.username ?? "—"}
        </Text>
      </Flex>

      {/* ── Stats row ── */}
      <Flex gap="md" wrap="wrap">
        {/* Total Invested */}
        <Box
          flex={1}
          miw={200}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Text fz="sm" c="slate.5" mb={4}>
            Total Invested
          </Text>
          {statsLoading ? (
            <Skeleton height={22} width={120} />
          ) : (
            <Text fw={700} fz="lg">
              {stats ? formatINR(stats.totalInvested) : "—"}
            </Text>
          )}
        </Box>

        {/* Current Value */}
        <Box
          flex={1}
          miw={200}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Text fz="sm" c="slate.5" mb={4}>
            Current Value
          </Text>
          {statsLoading ? (
            <Skeleton height={22} width={120} />
          ) : (
            <Flex align="center" gap="xs">
              <Text fw={700} fz="lg">
                {stats ? formatINR(stats.currentValue) : "—"}
              </Text>
              {absoluteReturn !== null && absoluteReturnPct !== null && (
                <Badge
                  color={absoluteReturn >= 0 ? "green" : "red"}
                  variant="light"
                  leftSection={absoluteReturn >= 0 ? <IconTrendingUp size={12} /> : <IconTrendingDown size={12} />}
                  size="sm"
                >
                  {absoluteReturn >= 0 ? "+" : ""}
                  {absoluteReturnPct.toFixed(2)}%
                </Badge>
              )}
            </Flex>
          )}
        </Box>

        {/* XIRR */}
        <Box
          flex={1}
          miw={200}
          px="lg"
          py="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Text fz="sm" c="slate.5" mb={4}>
            XIRR (annualised)
          </Text>
          {statsLoading ? (
            <Skeleton height={22} width={80} />
          ) : (
            <Text fw={700} fz="lg" c={xirrValue === null ? undefined : isPositiveXirr ? "green" : "red"}>
              {xirrValue !== null ? `${xirrValue.toFixed(2)}%` : "—"}
            </Text>
          )}
        </Box>
      </Flex>

      {/* ── Cache notice ── */}
      {statsLoadedAt && !statsLoading && (
        <Flex align="center" gap={6} mt="sm">
          <IconClock size={14} color="var(--mantine-color-gray-5)" />
          <Tooltip label="Stats are computed nightly at 9 PM IST and cached in the database" withArrow>
            <Text fz="xs" c="dimmed" style={{ cursor: "default" }}>
              Stats as of {statsLoadedAt.toLocaleTimeString("en-US")} — updates nightly at 9 PM IST
            </Text>
          </Tooltip>
        </Flex>
      )}

      <LogoutModal opened={logoutOpened} onClose={() => setLogoutOpened(false)} onConfirm={handleLogout} />
    </Flex>
  );
}
